import { CategoryDocument } from '@models/category';
import Accordion from '@ui/Accordion';
import Checkbox from '@ui/Checkbox';
import Input from '@ui/Input';
import Toggle from '@ui/Toggle';
import { getGenderLabel } from '@util/createListingHelpers';
import { PageSideFilters, PageType } from '@util/hooks/useProductsPage';
import { getCountryLabelFromCode, isMobile } from '@util/index';
import { useAuth } from 'context/AuthContext';
import { Bike } from 'models/shared';
import { FacetCounts } from 'models/product';
import {
  BOOT_SIZES,
  Gender,
  LETTER_SIZES,
  PANT_SIZES,
  rideTypeChoices,
} from 'models/shared';
import { UserSizes } from 'models/user';
import Link from 'next/link';
import { useState } from 'react';
import CheckboxFilterSection from './sections/CheckboxFilterSection';
import MySizesGarage from './sections/MySizesGarage';
import PriceRangeFilterSection from './sections/PriceRangeFilterSection';
export interface FilterItem {
  key: string;
  label: string;
  selected?: boolean;
}

interface FilterProps {
  categories?: CategoryDocument[];
  facet_counts?: FacetCounts;
  mostly_parts?: boolean;
  filterState: PageSideFilters;
  allBrands?: string[];
  pageType?: PageType;
  handleFilterChange: (key: string, value: any) => void;
  handleCategoryChange: (
    category: string,
    level: 'ride_type' | 'category' | 'category1' | 'category2' | 'category3'
  ) => void;
  handleBikeSelection: ({
    bike,
    bikes,
  }: {
    bike?: Bike | null;
    bikes?: Bike[];
  }) => void;
  handleSizeChange: (sizes: UserSizes) => void;
}

const Filters = ({
  categories,
  filterState,
  handleFilterChange,
  handleCategoryChange,
  handleBikeSelection,
  handleSizeChange,
  mostly_parts,
  facet_counts,
  allBrands,
  pageType,
}: FilterProps) => {
  const { userDoc } = useAuth();
  const [brandFilter, setBrandFilter] = useState('');
  const brands = brandFilter
    ? allBrands?.map((b) => ({
        key: b,
        label: b,
        count: 0,
      })) ?? []
    : facet_counts?.brand?.map((b) => ({
        key: b.value,
        label: b.value,
        count: b.count,
      })) ?? [];

  const tire_count =
    facet_counts?.category2?.find((c) => c.value === 'Tires')?.count ?? 0;
  const mostly_tires =
    tire_count > 0 &&
    tire_count >
      (facet_counts?.category2?.reduce((acc, c) => acc + c.count, 0) ?? 0) *
        0.5;

  const pant_count =
    facet_counts?.category2?.find((c) => c.value === 'Pants')?.count ?? 0;
  const mostly_pants =
    pant_count > 0 &&
    pant_count >
      (facet_counts?.category2?.reduce((acc, c) => acc + c.count, 0) ?? 0) *
        0.5;

  const boot_count =
    facet_counts?.category1?.find((c) => c.value === 'Boots')?.count ?? 0;
  const mostly_boots =
    boot_count > 0 &&
    boot_count >
      (facet_counts?.category1?.reduce((acc, c) => acc + c.count, 0) ?? 0) *
        0.5;

  const helmet_count =
    facet_counts?.category1?.find((c) => c.value === 'Helmets')?.count ?? 0;
  const mostly_helmets =
    helmet_count > 0 &&
    helmet_count >
      (facet_counts?.category1?.reduce((acc, c) => acc + c.count, 0) ?? 0) *
        0.5;
  function formatFacetCounts(): {
    hierarchy: {
      mainCats: {
        value: string;
        count: number;
        order: string;
      }[];
      otherCats: {
        value: string;
        count: number;
        order: string;
      }[];
    };
  } {
    const cat1s =
      facet_counts?.category1?.map((cat) => ({
        value: cat.value,
        count: cat.count,
        highlighted: cat.highlighted,
        order: 'category1',
      })) ?? [];
    const cat2s =
      facet_counts?.category2?.map((cat) => ({
        value: cat.value,
        count: cat.count,
        highlighted: cat.highlighted,
        order: 'category2',
      })) ?? [];
    const cat3s =
      facet_counts?.category3?.map((cat) => ({
        value: cat.value,
        count: cat.count,
        highlighted: cat.highlighted,
        order: 'category3',
      })) ?? [];

    const hierarchy = {
      mainCats:
        facet_counts?.category?.map((cat) => ({
          value: cat.value,
          count: cat.count,
          highlighted: cat.highlighted,
          order: 'category',
        })) ?? [],
      otherCats: [...cat1s, ...cat2s, ...cat3s]
        .sort((a, b) => b.count - a.count)
        .filter(
          (c, i, self) => i === self.findIndex((t) => t.value === c.value)
        ),
    };
    return { hierarchy };
  }
  const formattedFacetCounts = formatFacetCounts();

  return (
    <div className="flex w-full flex-col rounded-brand bg-inherit">
      {userDoc?.country?.code && userDoc.country.code !== 'US' && (
        <div className="my-2 flex w-full justify-between rounded-2xl border-2 border-brand-secondary bg-brand-secondary-light px-3 py-4">
          <span className="text-[1.5rem] font-semibold">
            {`Ship to My Country (${userDoc?.country.code})`}
          </span>
          <Toggle
            enabled={
              filterState.shipTo?.includes(userDoc?.country.code) ?? false
            }
            onChange={() => {
              const newArr = [...(filterState.shipTo ?? [])];
              if (!newArr.includes(userDoc?.country.code)) {
                newArr.push(userDoc?.country.code);
              } else {
                newArr.splice(newArr.indexOf(userDoc?.country.code), 1); //deleting
              }
              handleFilterChange('shipTo', newArr);
            }}
          />
        </div>
      )}
      <div className="mb-8">
        <MySizesGarage
          userDoc={userDoc}
          facet_counts={facet_counts}
          filterState={filterState}
          handleBikeSelection={handleBikeSelection}
          handleSizeSelection={handleSizeChange}
        />
      </div>
      {isMobile() && (
        <CheckboxFilterSection
          title="Condition"
          filterOptions={['New', 'Used'].map((condition) => ({
            key: condition,
            label: condition,
            value: condition,
          }))}
          onChange={(values) => {
            handleFilterChange('conditions', values);
          }}
          filterState={filterState.conditions ?? []}
          startOpen
        />
      )}
      {pageType !== 'category' && (
        // ride types
        <CheckboxFilterSection
          title="Ride Types"
          filterOptions={rideTypeChoices.map((rt) => ({
            key: rt,
            label: rt,
          }))}
          startOpen
          onChange={(values) => handleFilterChange('rideTypes', values)}
          filterState={filterState.rideTypes ?? []}
          facet_counts={facet_counts?.ride_types}
          showSelectedFirst
        />
      )}
      {pageType !== 'category' && (
        <div className="border-b border-zinc-300">
          <Accordion
            title={'Categories'}
            titleClassName="text-[2.1rem] sm:text-[1.8rem] font-semibold"
          >
            <div className="flex max-h-[45rem] flex-col gap-4 overflow-y-auto overflow-x-hidden px-[1rem] pb-[2rem]">
              {formattedFacetCounts.hierarchy.otherCats.map((cat) => {
                return (
                  <button
                    key={cat.value}
                    className="text-left text-[1.6rem] font-medium transition-all duration-150 ease-linear hover:font-semibold hover:text-brand-secondary"
                    onClick={() => {
                      handleCategoryChange(
                        cat.value,
                        cat.order as
                          | 'ride_type'
                          | 'category'
                          | 'category1'
                          | 'category2'
                          | 'category3'
                      );
                    }}
                  >
                    {cat.value} ({cat.count})
                  </button>
                );
              })}
            </div>
          </Accordion>
        </div>
      )}
      {pageType === 'category' && !!categories?.length && (
        <div className="border-b border-zinc-300">
          <Accordion
            title={'Categories'}
            titleClassName="text-[2.1rem] sm:text-[1.8rem] font-semibold"
            startOpen={true}
          >
            <div className="flex max-h-[45rem] flex-col gap-4 overflow-y-auto overflow-x-hidden px-[1rem] pb-[2rem]">
              {categories.map((cat) => {
                return (
                  <Link
                    href={cat.full_slug}
                    key={cat.id}
                    className="text-left text-[1.6rem] font-medium transition-all duration-150 ease-linear hover:font-semibold hover:text-brand-secondary"
                  >
                    {cat.name}
                    {/* {cat.value} ({cat.count}) */}
                  </Link>
                );
              })}
            </div>
          </Accordion>
        </div>
      )}

      {mostly_parts && (
        <div className="flex w-full justify-between border-b border-zinc-300 px-3 py-4">
          <span className="text-[2.1rem] font-semibold sm:text-[1.8rem]">
            OEM Parts
          </span>

          <Toggle
            enabled={!!filterState.brands?.includes('OEM')}
            onChange={() => {
              const newArr = [...(filterState.brands ?? [])];
              if (!newArr.includes('OEM')) {
                newArr.push('OEM');
              } else {
                newArr.splice(newArr.indexOf('OEM'), 1); //deleting
              }
              handleFilterChange('brands', newArr);

              window.scrollTo({
                top: 0,
                behavior: 'smooth',
              });
            }}
          />
        </div>
      )}

      {!!brands?.length && (
        <CheckboxFilterSection
          title="Brands"
          filterOptions={brands
            .filter(
              (b) =>
                (!brandFilter ||
                  b.label.toLowerCase().includes(brandFilter.toLowerCase())) &&
                b.label !== 'OEM'
            )
            .map((b) => ({
              key: b.key,
              label: b.label,
              id: b.key,
            }))}
          showSelectedFirst
          facet_counts={facet_counts?.brand}
          onChange={(values) => {
            handleFilterChange('brands', values);
          }}
          filterState={filterState.brands ?? []}
          startOpen={true}
          noFiltersMessage="No brands found"
          noFiltersClassName="py-8"
        >
          <div className="mb-4 p-4">
            <Input
              placeholder={'Search All Brands'}
              type={'search'}
              iconClassName="!h-6 !w-6"
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
              className="!h-[3.8rem] !rounded-[1.5rem]"
            />
          </div>
        </CheckboxFilterSection>
      )}
      {!mostly_parts && !mostly_boots && (
        <CheckboxFilterSection
          title={mostly_helmets ? 'Helmet Size' : 'Size'}
          filterOptions={LETTER_SIZES.map((g) => ({
            key: g + '',
            label: g + '',
          }))}
          onChange={(values) => handleFilterChange('letterSizes', values)}
          startOpen={!!filterState.letterSizes?.length}
          filterState={filterState.letterSizes ?? []}
          facet_counts={facet_counts?.['variations.size']}
        />
      )}
      {mostly_boots && (
        <CheckboxFilterSection
          title="Boot Size"
          filterOptions={BOOT_SIZES[
            (filterState.genders?.length === 1
              ? filterState.genders[0]
              : 'U') as Gender
          ].map((g) => ({ key: g + '', label: g + '' }))}
          onChange={(values) => handleFilterChange('numberSizes', values)}
          filterState={filterState.numberSizes ?? []}
        />
      )}
      {mostly_pants && (
        <CheckboxFilterSection
          title="Pant Size"
          filterOptions={PANT_SIZES[
            (filterState.genders?.length === 1
              ? filterState.genders[0]
              : 'U') as Gender
          ].map((g) => ({ key: g + '', label: g + '' }))}
          onChange={(values) => handleFilterChange('numberSizes', values)}
          filterState={filterState.numberSizes ?? []}
        />
      )}
      {!mostly_parts && (
        <CheckboxFilterSection
          title="Gender / Age"
          filterOptions={['M', 'W', 'Y', 'U'].map((g) => ({
            value: g,
            key: g,
            label: getGenderLabel(g as Gender),
          }))}
          onChange={(values) => handleFilterChange('genders', values)}
          filterState={filterState.genders ?? []}
        />
      )}
      {!!facet_counts?.['specs.Width']?.length && mostly_tires && (
        <CheckboxFilterSection
          title="Width"
          filterOptions={facet_counts?.['specs.Width']
            ?.sort((a, b) => +b.value - +a.value)
            .map((item) => ({
              key: item.value,
              label: item.value,
              count: item.count,
            }))}
          onChange={(values) => handleFilterChange('widths', values)}
          filterState={filterState.widths ?? []}
          facet_counts={facet_counts?.['specs.Width']}
          showSelectedFirst
        />
      )}
      {!!facet_counts?.['specs.Aspect Ratio']?.length && mostly_tires && (
        <CheckboxFilterSection
          title="Aspect Ratio"
          filterOptions={facet_counts?.['specs.Aspect Ratio']
            ?.sort((a, b) => +b.value - +a.value)
            .map((item) => ({
              key: item.value,
              label: item.value,
              count: item.count,
            }))}
          onChange={(values) => handleFilterChange('aspectRatios', values)}
          filterState={filterState.aspectRatios ?? []}
          facet_counts={facet_counts?.['specs.Aspect Ratio']}
          showSelectedFirst
        />
      )}
      {!!facet_counts?.['specs.Rim Diameter']?.length && mostly_tires && (
        <CheckboxFilterSection
          title="Rim Diameter"
          filterOptions={facet_counts?.['specs.Rim Diameter']
            ?.sort((a, b) => +b.value - +a.value)
            .map((item) => ({
              key: item.value,
              label: item.value,
              count: item.count,
            }))}
          onChange={(values) => handleFilterChange('rimDiameters', values)}
          filterState={filterState.rimDiameters ?? []}
          facet_counts={facet_counts?.['specs.Rim Diameter']}
          showSelectedFirst
        />
      )}
      {!!facet_counts?.['specs.Construction']?.length && mostly_tires && (
        <CheckboxFilterSection
          title="Construction"
          filterOptions={facet_counts?.['specs.Construction'].map((item) => ({
            key: item.value,
            label: item.value,
            count: item.count,
          }))}
          onChange={(values) => handleFilterChange('constructions', values)}
          filterState={filterState.construction ?? []}
          facet_counts={facet_counts?.['specs.Construction']}
          showSelectedFirst
        />
      )}
      {!!facet_counts?.['specs.Mounting Position']?.length && mostly_tires && (
        <CheckboxFilterSection
          title="Mounting Position"
          filterOptions={facet_counts?.['specs.Mounting Position']?.map(
            (item) => ({
              key: item.value,
              label: item.value,
              count: item.count,
            })
          )}
          onChange={(values) => handleFilterChange('mountingPositions', values)}
          filterState={filterState.mountingPositions ?? []}
          facet_counts={facet_counts?.['specs.Mounting Position']}
          showSelectedFirst
        />
      )}

      {!!facet_counts?.['specs.Tire Diameter']?.length && mostly_tires && (
        <CheckboxFilterSection
          title="Tire Diameter"
          filterOptions={facet_counts?.['specs.Tire Diameter']
            ?.sort((a, b) => +b.value - +a.value)
            .map((item) => ({
              key: item.value,
              label: item.value,
              count: item.count,
            }))}
          onChange={(values) => handleFilterChange('tireDiameters', values)}
          filterState={filterState.tireDiameters ?? []}
          facet_counts={facet_counts?.['specs.Tire Diameter']}
          showSelectedFirst
        />
      )}
      <div className="mt-4 border-b border-zinc-300">
        <PriceRangeFilterSection
          min={filterState.minPrice}
          max={filterState.maxPrice}
          title="Price"
          titleClassName="text-[2.1rem] sm:text-[1.8rem]"
          onChange={(min, max) => {
            handleFilterChange('minPrice', min);
            handleFilterChange('maxPrice', max);
          }}
          startOpen={!!filterState.minPrice || !!filterState.maxPrice}
        />
      </div>
      <CheckboxFilterSection
        title="Ships To"
        filterOptions={['US', 'CA', 'MX', 'PR', 'EU', 'AU', 'SAM'].map(
          (code) => ({
            key: code,
            value: code,
            label: getCountryLabelFromCode(code),
          })
        )}
        onChange={(values) => {
          handleFilterChange('shipTo', values);
        }}
        filterState={filterState.shipTo ?? []}
      />

      <CheckboxFilterSection
        title="Ships From"
        filterOptions={['US', 'CA', 'MX', 'PR', 'EU', 'AU', 'SAM'].map(
          (code) => ({
            key: code,
            value: code,
            label: getCountryLabelFromCode(code),
          })
        )}
        onChange={(values) => {
          handleFilterChange('shipFrom', values);
        }}
        filterState={filterState.shipFrom ?? []}
      />
      <Accordion
        startOpen
        title="Show Only"
        titleClassName="text-[2.1rem] sm:text-[1.8rem]"
      >
        <div className="flex flex-col gap-y-2 p-[1rem]">
          <Checkbox
            label="On Sale"
            onChange={() => {
              handleFilterChange('onsale', !filterState.onsale);
            }}
            selected={!!filterState.onsale}
          />

          <Checkbox
            label="Sold Items"
            onChange={() => {
              handleFilterChange('recentlySold', !filterState.recentlySold);
            }}
            selected={!!filterState.recentlySold}
          />
        </div>
      </Accordion>

      <div className="mb-4 border-b border-zinc-300" />
    </div>
  );
};

export default Filters;
